import { Component, DoCheck, Inject, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CheckerComments, ValidationLevel as ValRes } from '../../core/types';
import { commentTimeString } from '../../core/utils/date-utils';
import { IAuthContext } from '../../core/interfaces/auth-context.interface';
import { IValidationService } from '../../core/interfaces/validation-service.interface';
import { AUTH_CONTEXT, VALIDATION_SERVICE } from '../../core/tokens';
import { CommentsModalComponent } from '../comments/comments-modal.component';
import { ControlsIconsModule } from '../icons.module';

interface FieldComment {
	fieldName: string;
	displayName: string;
	comments: CheckerComments;
}

/**
 * Zeigt Kommentare für eine Tabellenzeile an.
 * Wird als zusätzliche <tr> nach der Datenzeile eingefügt.
 *
 * Verwendung:
 * <tr *ngIf="hasRowComments(i)" wd-table-row-comments [rowIndex]="i" [tiefe]="row.tiefe" [fieldPrefixes]="['STiefe', 'SMächtigkeit', 'Substrat']"></tr>
 */
@Component({
	standalone: true,
	selector: '[wd-table-row-comments]',
	template: `
		@if (fieldComments.length > 0) {
			<td [attr.colspan]="colspan" class="comment-row-cell">
				@for (fc of fieldComments; track fc.fieldName) {
					<div class="field-comment" [ngClass]="commentsClass(fc.comments)" (click)="onCommentsClicked(fc)">
						<div class="d-flex align-items-center flex-nowrap">
							<button type="button" class="btn comment-open-btn"
								[ngClass]="commentsClass(fc.comments, fc.comments.resolved ? 'btn-outline-' : 'btn-')"
								(click)="onOpenCommentsClicked(fc); $event.stopPropagation()">
								<bi name="chat-left-text"></bi>
							</button>
							<span class="field-label">{{ fc.displayName }}:</span>
							<span class="comment-text">{{ fc.comments.comments[0].msg }}</span>
							<span class="comment-stamp">
								{{ fc.comments.comments[0].name }} · {{ commentTimeString(fc.comments.comments[0].time) }}
							</span>
						</div>
					</div>
				}
			</td>
		}
	`,
	styles: [`
		.comment-row-cell {
			padding: 0 !important;
			background-color: transparent !important;
		}
		.field-comment {
			padding: 0.2rem 0.5rem;
			margin: 0;
			cursor: pointer;
			display: flex;
			align-items: center;
		}
		.field-comment.danger {
			background-color: #f8d7da;
		}
		.field-comment.warning {
			background-color: #fff3cd;
		}
		.field-comment.message {
			background-color: #d1ecf1;
		}
		.field-comment.rubber {
			cursor: url('/assets/rubber.svg') 8 24, auto;
		}
		.field-label {
			font-weight: 600;
			margin-right: 0.5rem;
			white-space: nowrap;
		}
		.comment-text {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.comment-stamp {
			font-size: 0.75rem;
			color: #6c757d;
			margin-left: 0.5rem;
			white-space: nowrap;
		}
		.comment-open-btn {
			flex-shrink: 0;
			padding: 0.1rem 0.3rem;
			margin-right: 0.3rem;
			font-size: 0.75rem;
		}
	`],
	imports: [CommonModule, ControlsIconsModule]
})
export class TableRowCommentsComponent implements DoCheck {
	@Input() rowIndex = 0;
	@Input() tiefe?: number;
	@Input() fieldPrefixes: string[] = [];
	@Input() colspan = 1;
	@Input() fieldNameMap: Record<string, string> = {};
	@Input() depthUnit = 'cm';

	fieldComments: FieldComment[] = [];
	commentTimeString = commentTimeString;

	private modalService = inject(NgbModal);

	constructor(
		@Inject(AUTH_CONTEXT) private authContext: IAuthContext,
		@Inject(VALIDATION_SERVICE) private vs: IValidationService
	) {}

	ngDoCheck(): void {
		this.updateFieldComments();
	}

	private updateFieldComments(): void {
		const result: FieldComment[] = [];
		const suffix = this.tiefe !== undefined ? `(${this.tiefe}${this.depthUnit})` : '';

		for (const prefix of this.fieldPrefixes) {
			const fieldName = `${prefix}[${this.rowIndex}]${suffix}`;
			const comments = this.vs.hasComments(fieldName);

			if (comments && !comments.resolved) {
				result.push({
					fieldName,
					displayName: this.getDisplayName(prefix),
					comments
				});
			}
		}

		// Auch Felder ohne Suffix prüfen (z.B. STiefe, HTiefe)
		for (const prefix of this.fieldPrefixes) {
			const fieldName = `${prefix}[${this.rowIndex}]`;
			if (fieldName.endsWith(suffix)) continue; // Schon oben geprüft

			const comments = this.vs.hasComments(fieldName);
			if (comments && !comments.resolved) {
				if (!result.some(fc => fc.fieldName === fieldName)) {
					result.push({
						fieldName,
						displayName: this.getDisplayName(prefix),
						comments
					});
				}
			}
		}

		this.fieldComments = result;
	}

	private getDisplayName(prefix: string): string {
		return this.fieldNameMap[prefix] || prefix;
	}

	commentsClass(comments: CheckerComments, prefix = ''): string {
		const rubber = this.authContext.pen === 'rubber' ? 'rubber' : '';
		switch (comments.type) {
			case ValRes.danger: return `${rubber} ${prefix}danger`;
			case ValRes.warning: return `${rubber} ${prefix}warning`;
			case ValRes.ok: return `${rubber} ${prefix}message`;
			default: return '';
		}
	}

	onOpenCommentsClicked(fc: FieldComment): void {
		const modalRef = this.modalService.open(CommentsModalComponent);
		modalRef.componentInstance.comments = fc.comments;
		modalRef.componentInstance.name = fc.fieldName;
		modalRef.closed.subscribe((value?: CheckerComments | 'saved') => {
			if (value === 'saved') {
				return;
			}
			if (!value) {
				this.vs.clearComments(fc.fieldName);
				return;
			}
			const existing = this.vs.hasComments(fc.fieldName);
			if (!existing) {
				return;
			}
			this.vs.resetComments(fc.fieldName, value);
		});
	}

	onCommentsClicked(fc: FieldComment): void {
		if (this.authContext.pen === 'rubber') {
			this.vs.clearComments(fc.fieldName);
		}
	}
}
